import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import {
  DOMAINES_PRESCOLAIRE, APPRECIATIONS_AEA,
  getTrimestreFromDate,
} from '../utils/cycle.helpers';

// ── Fiche d'observation Pré-Scolaire ─────────────────
export default function EvaluationPrescolaire() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const preClassId = params.get('classId') || '';

  const [classes, setClasses]     = useState([]);
  const [students, setStudents]   = useState([]);
  const [subjects, setSubjects]   = useState([]); // matières libres créées
  const [periods, setPeriods]     = useState([]);
  const [year, setYear]           = useState(null);
  const [classId, setClassId]     = useState(preClassId);
  const [periodId, setPeriodId]   = useState('');
  const [date, setDate]           = useState(new Date().toISOString().slice(0, 10));
  const [domaines, setDomaines]   = useState(DOMAINES_PRESCOLAIRE.map((d) => ({ ...d, active: true })));
  const [matieresLibres, setMatieresLibres] = useState([]);
  const [observations, setObservations] = useState({}); // { studentId: { domaineCode: 'A'|'EA'|'NA', comment } }
  const [commentaireGeneral, setCommentaireGeneral] = useState('');
  const [tab, setTab] = useState('saisie');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [newMatiere, setNewMatiere] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/catalog/classes'),
      api.get('/catalog/periods'),
      api.get('/catalog/active-year'),
    ]).then(([cls, per, yr]) => {
      // Filtrer classes Pré-Scolaire (PS/MS/GS)
      const prescolClasses = (cls || []).filter((c) => {
        const n = c.name.toUpperCase();
        return ['PS', 'MS', 'GS', 'PETITE', 'MOYENNE', 'GRANDE', 'PRÉ'].some((k) => n.includes(k))
          || c.level?.cycle?.name?.toUpperCase().includes('PRÉ')
          || c.level?.cycle?.name?.toUpperCase().includes('PRE');
      });
      setClasses(prescolClasses.length > 0 ? prescolClasses : (cls || []));
      if (!classId && prescolClasses[0]) setClassId(prescolClasses[0].id);
      setPeriods(per || []);
      if (per?.[0]) setPeriodId(per[0].id);
      setYear(yr);
    }).catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    if (!classId) return;
    api.get('/students').then((all) => {
      const inClass = (all || []).filter((s) =>
        (s.enrollments || []).some((en) => en.classId === classId && en.status === 'ACTIVE')
      );
      setStudents(inClass);
      // Initialiser les observations vides
      const obs = {};
      inClass.forEach((s) => {
        obs[s.id] = obs[s.id] || {};
        DOMAINES_PRESCOLAIRE.forEach((d) => {
          obs[s.id][d.code] = obs[s.id][d.code] || { appr: '', comment: '' };
        });
      });
      setObservations(obs);
    }).catch(() => {});
  }, [classId]);

  function setAppr(studentId, code, value) {
    setObservations((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [code]: { ...prev[studentId]?.[code], appr: value } },
    }));
  }

  function setComment(studentId, code, value) {
    setObservations((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [code]: { ...prev[studentId]?.[code], comment: value } },
    }));
  }

  function addMatiereLibre() {
    if (!newMatiere.trim()) return;
    const code = `LIBRE_${Date.now()}`;
    setMatieresLibres((prev) => [...prev, { code, label: newMatiere.trim(), active: true }]);
    setNewMatiere('');
  }

  function toggleDomaine(code) {
    setDomaines((prev) => prev.map((d) => d.code === code ? { ...d, active: !d.active } : d));
  }

  const activeDomaines = [
    ...domaines.filter((d) => d.active),
    ...matieresLibres.filter((m) => m.active),
  ];

  // Stats globales
  const statsParDomaine = activeDomaines.map((d) => {
    const vals = students.map((s) => observations[s.id]?.[d.code]?.appr).filter(Boolean);
    return {
      ...d,
      A:  vals.filter((v) => v === 'A').length,
      EA: vals.filter((v) => v === 'EA').length,
      NA: vals.filter((v) => v === 'NA').length,
      total: vals.length,
    };
  });

  async function save() {
    setSaving(true); setError('');
    try {
      // Résoudre periodId
      const autoT = getTrimestreFromDate(date);
      let pId = periodId;
      if (!pId && periods.length > 0) {
        const match = periods.find((p) => p.name?.includes(String(autoT)));
        pId = match?.id || periods[0]?.id;
      }
      // Sauvegarder comme une évaluation spéciale via registry
      const payload = {
        data: {
          type: 'PRESCOLAIRE',
          classId,
          periodId: pId,
          academicYearId: year?.id,
          date,
          domaines: activeDomaines.map((d) => d.code),
          observations,
          commentaireGeneral,
          status: 'PUBLIE',
        },
        label: `Observation Pré-Scolaire — ${date}`,
        searchText: `${classId} ${date}`,
        refDate: date,
      };
      await api.post('/registry/observations-prescolaire', payload);
      setSaved(true);
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  const className = classes.find((c) => c.id === classId)?.name || '—';

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
            <button className="btn-ghost btn-sm" onClick={() => navigate('/evaluations')}>← Retour</button>
            <span className="badge" style={{ background: '#fef3c7', color: '#d97706', fontSize: 11 }}>🎨 Pré-Scolaire</span>
          </div>
          <h1 style={{ fontSize: '1.2rem', fontWeight: 600 }}>Fiche d'observation — {className}</h1>
          <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
            Appréciations par domaine d'activités (Acquis · En cours d'Acquisition · Non Acquis)
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {saved && <span className="badge green">Enregistré ✓</span>}
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 20px' }}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}

      {/* Filtres */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div className="field" style={{ margin: 0 }}><label>Classe</label>
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}><label>Trimestre</label>
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)}>
            <option value="">— Auto ({`T${getTrimestreFromDate(date)}`}) —</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="field" style={{ margin: 0 }}><label>Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'saisie' ? 'active' : ''}`} onClick={() => setTab('saisie')}>Saisie des observations</button>
        <button className={`tab ${tab === 'domaines' ? 'active' : ''}`} onClick={() => setTab('domaines')}>Domaines & matières</button>
        <button className={`tab ${tab === 'stats' ? 'active' : ''}`} onClick={() => setTab('stats')}>Statistiques</button>
      </div>

      {tab === 'domaines' && (
        <div className="card">
          <div className="card-head"><h2>Domaines d'activités</h2></div>
          <div className="card-body" style={{ padding: 16 }}>
            <div className="fieldset-title">Domaines UEMOA</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {domaines.map((d) => (
                <div key={d.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '0.5px solid var(--line)', borderRadius: 8 }}>
                  <input type="checkbox" checked={d.active} onChange={() => toggleDomaine(d.code)} id={d.code} />
                  <label htmlFor={d.code} style={{ cursor: 'pointer', flex: 1, fontSize: 13 }}>{d.label}</label>
                </div>
              ))}
            </div>

            <div className="fieldset-title">Matières libres (optionnel)</div>
            <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 10 }}>
              Ajoutez des matières spécifiques à votre établissement (Anglais, Informatique, Religion, etc.)
            </p>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input value={newMatiere} onChange={(e) => setNewMatiere(e.target.value)}
                placeholder="Nom de la matière…" onKeyDown={(e) => e.key === 'Enter' && addMatiereLibre()} style={{ flex: 1 }} />
              <button className="btn btn-sm" onClick={addMatiereLibre}>+ Ajouter</button>
            </div>
            {matieresLibres.map((m) => (
              <div key={m.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '0.5px solid var(--primary)', borderRadius: 8, marginBottom: 6, background: 'var(--primary-soft)' }}>
                <input type="checkbox" checked={m.active} onChange={() => setMatieresLibres((p) => p.map((x) => x.code === m.code ? { ...x, active: !x.active } : x))} />
                <span style={{ flex: 1, fontSize: 13 }}>{m.label}</span>
                <button className="link-danger" onClick={() => setMatieresLibres((p) => p.filter((x) => x.code !== m.code))}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'saisie' && (
        <div className="card">
          <div className="card-head">
            <h2>Observations par élève</h2>
            <div style={{ display: 'flex', gap: 6 }}>
              {APPRECIATIONS_AEA.map((a) => (
                <span key={a.code} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 4, background: a.color + '20', color: a.color, fontWeight: 600 }}>
                  {a.code} = {a.label}
                </span>
              ))}
            </div>
          </div>
          <div className="card-body" style={{ padding: 0, overflowX: 'auto' }}>
            {students.length === 0 ? <div className="empty">Aucun élève dans cette classe.</div> : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-soft)' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', minWidth: 160, borderBottom: '0.5px solid var(--line)' }}>Élève</th>
                    {activeDomaines.map((d) => (
                      <th key={d.code} style={{ padding: '8px 6px', textAlign: 'center', minWidth: 120, borderBottom: '0.5px solid var(--line)', fontSize: 11 }}>
                        {d.label}
                      </th>
                    ))}
                    <th style={{ padding: '8px 12px', minWidth: 160, borderBottom: '0.5px solid var(--line)' }}>Appréciation générale</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s) => (
                    <tr key={s.id} style={{ borderBottom: '0.5px solid var(--line-soft)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                        {s.lastName} {s.firstName}
                        <div style={{ fontSize: 10, color: 'var(--muted)' }}>{s.matricule}</div>
                      </td>
                      {activeDomaines.map((d) => {
                        const val = observations[s.id]?.[d.code]?.appr || '';
                        return (
                          <td key={d.code} style={{ padding: '6px 4px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                              {APPRECIATIONS_AEA.map((a) => (
                                <button key={a.code}
                                  onClick={() => setAppr(s.id, d.code, val === a.code ? '' : a.code)}
                                  style={{
                                    width: 32, height: 28, borderRadius: 6, border: '1px solid',
                                    fontSize: 10, fontWeight: 700, cursor: 'pointer',
                                    background: val === a.code ? a.color : 'transparent',
                                    borderColor: a.color,
                                    color: val === a.code ? '#fff' : a.color,
                                  }}>
                                  {a.code}
                                </button>
                              ))}
                            </div>
                          </td>
                        );
                      })}
                      <td style={{ padding: '6px 12px' }}>
                        <input style={{ width: '100%', fontSize: 12 }}
                          value={observations[s.id]?.GENERAL?.comment || ''}
                          onChange={(e) => setComment(s.id, 'GENERAL', e.target.value)}
                          placeholder="Appréciation…" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {tab === 'stats' && (
        <div className="card">
          <div className="card-head"><h2>Statistiques par domaine</h2></div>
          <div className="card-body">
            {statsParDomaine.length === 0 ? <div className="empty">Aucune donnée.</div> : (
              <div className="table-wrap"><table>
                <thead>
                  <tr>
                    <th>Domaine</th>
                    <th className="num" style={{ color: '#16a34a' }}>Acquis (A)</th>
                    <th className="num" style={{ color: '#f59e0b' }}>En cours (EA)</th>
                    <th className="num" style={{ color: '#dc2626' }}>Non Acquis (NA)</th>
                    <th className="num">Taux Acquis</th>
                  </tr>
                </thead>
                <tbody>{statsParDomaine.map((d) => {
                  const taux = d.total > 0 ? Math.round(d.A / d.total * 100) : 0;
                  return (
                    <tr key={d.code}>
                      <td><b>{d.label}</b></td>
                      <td className="num" style={{ color: '#16a34a', fontWeight: 700 }}>{d.A}</td>
                      <td className="num" style={{ color: '#f59e0b', fontWeight: 700 }}>{d.EA}</td>
                      <td className="num" style={{ color: '#dc2626', fontWeight: 700 }}>{d.NA}</td>
                      <td className="num">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                          <div style={{ width: 60, height: 6, background: 'var(--line)', borderRadius: 3 }}>
                            <div style={{ height: '100%', width: `${taux}%`, background: taux >= 70 ? '#16a34a' : taux >= 40 ? '#f59e0b' : '#dc2626', borderRadius: 3 }} />
                          </div>
                          <span style={{ minWidth: 35 }}>{taux}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table></div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
